import { LOCALE_NAMES } from "@repo/shared";
import type { FlowProductDocumentsData, LocaleCode } from "@repo/shared/types";
import { FileText } from "lucide-react";
import { useEffect, useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import { getFlowInstanceProductDocuments } from "@/lib/api/product-documents";
import { useLocalize } from "@/lib/i18n/use-localize";
import { useT } from "@/lib/i18n/use-t";
import { T } from "./product-documents-section.i18n";

type ProductDocumentsSectionProps = {
  flowInstanceId: string;
};

export function ProductDocumentsSection({ flowInstanceId }: ProductDocumentsSectionProps) {
  const [data, setData] = useState<FlowProductDocumentsData | null>(null);
  const [loading, setLoading] = useState(true);
  const localize = useLocalize();
  const t = useT(T);

  useEffect(() => {
    getFlowInstanceProductDocuments(flowInstanceId)
      .then((result) => {
        setData(result);
      })
      .catch(() => {
        // Leave data null — the section renders its empty state.
      })
      .finally(() => {
        setLoading(false);
      });
  }, [flowInstanceId]);

  if (loading) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.productDocuments}</CardTitle>
          <CardDescription>{t.loading}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (!data || data.documentTypes.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.productDocuments}</CardTitle>
          <CardDescription>{t.noDocumentTypes}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  if (data.products.length === 0) {
    return (
      <Card>
        <CardHeader>
          <CardTitle>{t.productDocuments}</CardTitle>
          <CardDescription>{t.noProducts}</CardDescription>
        </CardHeader>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t.productDocuments}</CardTitle>
        <CardDescription>{t.documentsDescription}</CardDescription>
      </CardHeader>
      <CardContent>
        <Table variant="data" surface={false}>
          <TableHeader>
            <TableRow>
              <TableHead className="min-w-[200px]">{t.product}</TableHead>
              {data.documentTypes.map((dt) => (
                <TableHead key={dt.id} className="text-center min-w-[100px]">
                  {localize(dt.nameI18n)}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {data.products.map((product) => (
              <TableRow key={product.productId}>
                <TableCell>
                  <div>
                    <span className="font-medium">{product.productName}</span>
                    {product.isin && (
                      <span className="text-xs text-muted-foreground ml-2">({product.isin})</span>
                    )}
                  </div>
                </TableCell>
                {product.documents.map((doc) => (
                  <TableCell key={doc.documentTypeId} className="text-center">
                    {doc.hasDocument && doc.documentUrl ? (
                      <Tooltip>
                        <TooltipTrigger asChild>
                          <a
                            href={doc.documentUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center justify-center hover:opacity-70"
                            aria-label={`${t.open} ${localize(doc.documentTypeNameI18n)}`}
                          >
                            <FileText className="h-5 w-5" />
                          </a>
                        </TooltipTrigger>
                        <TooltipContent>
                          {`${t.open} ${localize(doc.documentTypeNameI18n)}`}
                          {doc.language
                            ? ` · ${LOCALE_NAMES[doc.language as LocaleCode] ?? doc.language}`
                            : ""}
                        </TooltipContent>
                      </Tooltip>
                    ) : (
                      <span className="text-muted-foreground">—</span>
                    )}
                  </TableCell>
                ))}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>
  );
}
